<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import dayjs from 'dayjs';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import WorkforceAPI from 'dashboard/api/workforce';
import WorkforceButton from '../components/WorkforceButton.vue';
import WorkforceModal from '../components/WorkforceModal.vue';

const emit = defineEmits(['refresh-self-status']);
const { t } = useI18n();
const store = useStore();
const agents = useMapGetter('agents/getAgents');

const sessions = ref([]);
const policySettings = ref(null);
const loading = ref(false);
const settingsLoading = ref(false);
const settingsSaving = ref(false);
const expandedSessions = ref(new Set());
const liveStatus = ref([]);
const liveLoading = ref(false);
const now = ref(dayjs());
let ticker = null;

const settingsForm = reactive({
  minShiftHours: '',
  minBreakHours: '',
});

const policyModal = reactive({
  open: false,
});

const editModal = reactive({
  open: false,
  loading: false,
  sessionId: null,
  form: {
    startedAt: '',
    endedAt: '',
    breakHours: '',
    idleMinutes: '',
    managerNotes: '',
  },
});

const filters = reactive({
  userId: 'all',
  from: null,
  to: null,
});

const todayIso = dayjs().format('YYYY-MM-DD');
filters.from = todayIso;
filters.to = todayIso;

const selectedDateRange = ref('today');
const selectedAnomalyDate = ref(todayIso);

const numberOrNull = value => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return null;
  return parsed;
};

const resetSettingsForm = () => {
  const current = policySettings.value || {};
  const shift = numberOrNull(current.min_shift_hours);
  const breakHours = numberOrNull(current.min_break_hours);
  settingsForm.minShiftHours = shift ?? '';
  settingsForm.minBreakHours = breakHours ?? '';
};

const fetchAttendanceSettings = async () => {
  settingsLoading.value = true;
  try {
    const { data } = await WorkforceAPI.getAttendanceSettings();
    policySettings.value = data || {};
    resetSettingsForm();
  } catch (error) {
    useAlert(t('WORKFORCE.ATTENDANCE.SETTINGS.TOASTS.LOAD_ERROR'));
  } finally {
    settingsLoading.value = false;
  }
};

const settingsDirty = computed(() => {
  if (!policySettings.value) {
    return Boolean(
      numberOrNull(settingsForm.minShiftHours) ||
        numberOrNull(settingsForm.minBreakHours)
    );
  }

  const currentShift = numberOrNull(policySettings.value.min_shift_hours);
  const currentBreak = numberOrNull(policySettings.value.min_break_hours);
  const formShift = numberOrNull(settingsForm.minShiftHours);
  const formBreak = numberOrNull(settingsForm.minBreakHours);

  return currentShift !== formShift || currentBreak !== formBreak;
});

const dateRangePresets = computed(() => [
  {
    key: 'today',
    label: t('WORKFORCE.ATTENDANCE.RANGES.TODAY'),
    getBounds: () => {
      const date = dayjs().format('YYYY-MM-DD');
      return { from: date, to: date };
    },
  },
  {
    key: 'yesterday',
    label: t('WORKFORCE.ATTENDANCE.RANGES.YESTERDAY'),
    getBounds: () => {
      const date = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
      return { from: date, to: date };
    },
  },
  {
    key: 'last7',
    label: t('WORKFORCE.ATTENDANCE.RANGES.LAST_7_DAYS'),
    getBounds: () => {
      const to = dayjs().format('YYYY-MM-DD');
      const from = dayjs().subtract(6, 'day').format('YYYY-MM-DD');
      return { from, to };
    },
  },
  {
    key: 'last30',
    label: t('WORKFORCE.ATTENDANCE.RANGES.LAST_30_DAYS'),
    getBounds: () => {
      const to = dayjs().format('YYYY-MM-DD');
      const from = dayjs().subtract(29, 'day').format('YYYY-MM-DD');
      return { from, to };
    },
  },
]);

const resolveRangeBounds = rangeKey => {
  const preset = dateRangePresets.value.find(item => item.key === rangeKey);
  if (preset) {
    return preset.getBounds();
  }
  return { from: filters.from, to: filters.to };
};

const setDateRange = (rangeKey, { silent = false } = {}) => {
  const bounds = resolveRangeBounds(rangeKey);
  let changed = false;
  if (bounds.from && filters.from !== bounds.from) {
    filters.from = bounds.from;
    changed = true;
  }
  if (bounds.to && filters.to !== bounds.to) {
    filters.to = bounds.to;
    changed = true;
  }
  selectedDateRange.value = rangeKey;
  if (bounds.to) {
    selectedAnomalyDate.value = bounds.to;
  }
  if (!changed && !silent) {
    fetchAttendance();
  }
};

const isRangeActive = rangeKey => selectedDateRange.value === rangeKey;

const policySummaryText = computed(() => {
  if (settingsLoading.value) {
    return t('WORKFORCE.ATTENDANCE.SETTINGS.SUMMARY_LOADING');
  }
  if (!policySettings.value) {
    return t('WORKFORCE.ATTENDANCE.SETTINGS.SUMMARY_EMPTY');
  }

  const shift = numberOrNull(policySettings.value.min_shift_hours);
  const breakMinutes = numberOrNull(policySettings.value.min_break_minutes);

  if (shift === null && breakMinutes === null) {
    return t('WORKFORCE.ATTENDANCE.SETTINGS.SUMMARY_DISABLED');
  }
  if (shift !== null && breakMinutes !== null) {
    return t('WORKFORCE.ATTENDANCE.SETTINGS.SUMMARY_BOTH', {
      shift: minutesToHumanLabel(shift * 60),
      break: minutesToHumanLabel(breakMinutes),
    });
  }
  if (shift !== null) {
    return t('WORKFORCE.ATTENDANCE.SETTINGS.SUMMARY_SHIFT', {
      shift: minutesToHumanLabel(shift * 60),
    });
  }
  return t('WORKFORCE.ATTENDANCE.SETTINGS.SUMMARY_BREAK', {
    break: minutesToHumanLabel(breakMinutes),
  });
});

const openPolicyModal = () => {
  resetSettingsForm();
  policyModal.open = true;
};

const closePolicyModal = () => {
  policyModal.open = false;
};

const clearDateFilters = () => {
  filters.from = null;
  filters.to = null;
  selectedDateRange.value = 'custom';
  selectedAnomalyDate.value = null;
  fetchLiveStatus();
};

const handleRefresh = async () => {
  await Promise.all([fetchAttendance(), fetchLiveStatus()]);
};

setDateRange('today', { silent: true });

const saveAttendanceSettings = async () => {
  settingsSaving.value = true;
  try {
    const payload = {};
    const shiftHours = numberOrNull(settingsForm.minShiftHours);
    const breakHours = numberOrNull(settingsForm.minBreakHours);

    payload.min_shift_hours = shiftHours;
    payload.min_break_minutes =
      breakHours === null ? null : Math.round(breakHours * 60);

    const { data } = await WorkforceAPI.updateAttendanceSettings(payload);
    policySettings.value = data || {};
    resetSettingsForm();
    closePolicyModal();
    await fetchAttendance();
    useAlert(t('WORKFORCE.ATTENDANCE.SETTINGS.TOASTS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('WORKFORCE.ATTENDANCE.SETTINGS.TOASTS.SAVE_ERROR'));
  } finally {
    settingsSaving.value = false;
  }
};

const fetchAttendance = async () => {
  loading.value = true;
  try {
    const params = {};
    if (filters.userId !== 'all') params.user_id = filters.userId;
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    const response = await WorkforceAPI.getAttendanceLogs(params);
    sessions.value = response.data || [];
    if (filters.to) {
      selectedAnomalyDate.value = filters.to;
    }
  } catch (error) {
    useAlert(t('WORKFORCE.ATTENDANCE.TOASTS.FETCH_ERROR'));
  } finally {
    loading.value = false;
  }
};

const fetchLiveStatus = async () => {
  liveLoading.value = true;
  try {
    const userIds =
      filters.userId === 'all'
        ? agents.value.map(a => a.id).join(',')
        : filters.userId;
    if (!userIds) {
      liveStatus.value = [];
      return;
    }
    const response = await WorkforceAPI.getLiveStatus({ user_ids: userIds });
    liveStatus.value = response?.agents || [];
  } catch (error) {
    useAlert(t('WORKFORCE.ATTENDANCE.TOASTS.LIVE_STATUS_ERROR'));
  } finally {
    liveLoading.value = false;
  }
};

const toggleSessionExpansion = sessionId => {
  const draft = new Set(expandedSessions.value);
  if (draft.has(sessionId)) {
    draft.delete(sessionId);
  } else {
    draft.add(sessionId);
  }
  expandedSessions.value = draft;
};

const isSessionExpanded = sessionId => expandedSessions.value.has(sessionId);

const formatDuration = seconds => {
  if (!seconds || Number.isNaN(seconds)) return '--';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const parts = [];
  if (hrs) parts.push(`${hrs}h`);
  if (mins || !hrs) parts.push(`${mins}m`);
  return parts.join(' ');
};

const formatDateTime = ts => {
  if (!ts) return '--';
  return new Date(ts).toLocaleString();
};

const minutesToHumanLabel = value => {
  if (value === null || value === undefined) return '--';
  const minutes = Number(value);
  if (Number.isNaN(minutes)) return '--';
  const rounded = Math.round(minutes * 10) / 10;
  const hrs = Math.floor(rounded / 60);
  const residual = Math.round((rounded - hrs * 60) * 10) / 10;
  const parts = [];
  if (hrs) parts.push(`${hrs}h`);
  if (!hrs || residual > 0) {
    const label = Number.isInteger(residual)
      ? `${residual}m`
      : `${residual.toFixed(1)}m`;
    parts.push(label);
  }
  if (!parts.length) {
    parts.push('0m');
  }
  return parts.join(' ');
};

const formatHours = seconds => {
  if (!seconds || Number.isNaN(seconds)) return '0.0';
  return (seconds / 3600).toFixed(1);
};

const anomalyLabel = anomaly => {
  if (!anomaly?.code) return '';
  switch (anomaly.code) {
    case 'short_shift':
      return t('WORKFORCE.ATTENDANCE.ANOMALIES.SHORT_SHIFT', {
        required: minutesToHumanLabel(anomaly.required_minutes),
        actual: minutesToHumanLabel(anomaly.actual_minutes),
      });
    case 'insufficient_break':
      return t('WORKFORCE.ATTENDANCE.ANOMALIES.INSUFFICIENT_BREAK', {
        required: minutesToHumanLabel(anomaly.required_minutes),
        actual: minutesToHumanLabel(anomaly.actual_minutes),
      });
    default:
      return anomaly.code;
  }
};

const sessionRowClass = session =>
  session?.anomalies?.length && session.status !== 'open'
    ? 'bg-red-3/20 ring-1 ring-red-7/50 dark:bg-red-7/15 dark:ring-red-5/50'
    : '';

const anomalyBuckets = computed(() => {
  const buckets = {};
  sessions.value.forEach(session => {
    if (!session?.anomalies?.length) return;
    const baseTimestamp = session.started_at || session.ended_at;
    if (!baseTimestamp) return;
    const dateKey = dayjs(baseTimestamp).format('YYYY-MM-DD');
    if (!buckets[dateKey]) {
      buckets[dateKey] = [];
    }
    session.anomalies.forEach((anomaly, index) => {
      buckets[dateKey].push({
        id: `${session.id}-${anomaly.code}-${index}`,
        session,
        anomaly,
      });
    });
  });
  return buckets;
});

const selectedAnomalyDateLabel = computed(() => {
  if (!selectedAnomalyDate.value) return '--';
  const parsed = dayjs(selectedAnomalyDate.value);
  return parsed.isValid()
    ? parsed.format('MMM D, YYYY')
    : selectedAnomalyDate.value;
});

const selectedAnomalies = computed(() => {
  const records = anomalyBuckets.value[selectedAnomalyDate.value] || [];
  return records.map(({ id, session, anomaly }) => {
    const workSummary =
      anomaly.code === 'short_shift'
        ? {
            actual: minutesToHumanLabel(anomaly.actual_minutes),
            required: minutesToHumanLabel(anomaly.required_minutes),
          }
        : null;
    const breakSummary =
      anomaly.code === 'insufficient_break'
        ? {
            actual: minutesToHumanLabel(anomaly.actual_minutes),
            required: minutesToHumanLabel(anomaly.required_minutes),
          }
        : null;

    return {
      id,
      anomaly,
      anomalyLabel: anomalyLabel(anomaly),
      agentName: agentName(session.user_id),
      status: session.status,
      statusLabel: session.status
        ? t('WORKFORCE.ATTENDANCE.ANOMALY_PANEL.STATUS', {
            status: session.status.replace('_', ' '),
          })
        : null,
      startedAt: session.started_at
        ? dayjs(session.started_at).format('MMM D, HH:mm')
        : '--',
      endedAt: session.ended_at
        ? dayjs(session.ended_at).format('MMM D, HH:mm')
        : t('WORKFORCE.ATTENDANCE.ANOMALY_PANEL.IN_PROGRESS'),
      workSummary,
      breakSummary,
      breakDuration: session.total_break_seconds
        ? minutesToHumanLabel(session.total_break_seconds / 60)
        : t('WORKFORCE.ATTENDANCE.ANOMALY_PANEL.NO_BREAK_LOGGED'),
    };
  });
});

const selectSummaryDay = summary => {
  if (!summary?.date || summary.date === 'untracked') return;
  selectedAnomalyDate.value = summary.date;
};

const sessionsByUser = computed(() => {
  const bucket = new Map();
  sessions.value.forEach(session => {
    if (!session?.user_id) return;
    const list = bucket.get(session.user_id) || [];
    list.push(session);
    bucket.set(session.user_id, list);
  });
  return bucket;
});

const uniqueAgentsCount = computed(() => sessionsByUser.value.size);

const sessionsWithAnomalies = computed(() =>
  sessions.value.filter(
    session => Array.isArray(session.anomalies) && session.anomalies.length
  )
);

const policyAlertAgentsCount = computed(() => {
  const ids = new Set();
  sessionsWithAnomalies.value.forEach(session => ids.add(session.user_id));
  return ids.size;
});

const policyAlertTotal = computed(() =>
  sessionsWithAnomalies.value.reduce(
    (count, session) => count + session.anomalies.length,
    0
  )
);

const computeEffectiveWorkSeconds = session => {
  if (!session?.started_at) return 0;
  if (session?.total_work_seconds && session.ended_at) {
    return session.total_work_seconds;
  }
  const start = dayjs(session.started_at);
  if (!start.isValid()) return 0;
  const end = session.ended_at ? dayjs(session.ended_at) : dayjs();
  if (!end.isValid()) return 0;
  const diff = Math.max(end.diff(start, 'second'), 0);
  const breakSeconds = session.total_break_seconds || 0;
  return Math.max(diff - breakSeconds, 0);
};

const computeEffectiveBreakSeconds = session => {
  const base = session.total_break_seconds || 0;
  if (!session?.metadata?.active_break_started_at || session.ended_at) {
    return base;
  }
  const started = dayjs(session.metadata.active_break_started_at);
  if (!started.isValid()) return base;
  const diff = Math.max(dayjs().diff(started, 'second'), 0);
  return base + diff;
};

const totalWorkSeconds = computed(() =>
  sessions.value.reduce(
    (sum, session) => sum + computeEffectiveWorkSeconds(session),
    0
  )
);

const totalBreakSeconds = computed(() =>
  sessions.value.reduce(
    (sum, session) => sum + computeEffectiveBreakSeconds(session),
    0
  )
);

const liveOnShift = computed(() =>
  liveStatus.value.filter(record => record.session && !record.session.ended_at)
);

const liveOnBreak = computed(() =>
  liveOnShift.value.filter(record => record.session?.active_break)
);

const scheduledButAbsent = computed(() =>
  liveStatus.value.filter(
    record =>
      record.scheduled_now &&
      (!record.session || record.session.status !== 'open')
  )
);

const hrMetricCards = computed(() => [
  {
    id: 'team_check_ins',
    label: t('WORKFORCE.ATTENDANCE.HR.METRICS.CHECK_INS.LABEL'),
    value: String(uniqueAgentsCount.value),
    hint: t('WORKFORCE.ATTENDANCE.HR.METRICS.CHECK_INS.HINT', {
      count: uniqueAgentsCount.value,
    }),
  },
  {
    id: 'active_now',
    label: t('WORKFORCE.ATTENDANCE.HR.METRICS.ACTIVE_NOW.LABEL'),
    value: String(liveOnShift.value.length),
    hint: t('WORKFORCE.ATTENDANCE.HR.METRICS.ACTIVE_NOW.HINT'),
  },
  {
    id: 'active_breaks',
    label: t('WORKFORCE.ATTENDANCE.HR.METRICS.ACTIVE_BREAKS.LABEL'),
    value: String(liveOnBreak.value.length),
    hint: t('WORKFORCE.ATTENDANCE.HR.METRICS.ACTIVE_BREAKS.HINT'),
  },
  {
    id: 'scheduled_absent',
    label: t('WORKFORCE.ATTENDANCE.HR.METRICS.SCHEDULED_ABSENT.LABEL'),
    value: String(scheduledButAbsent.value.length),
    hint: t('WORKFORCE.ATTENDANCE.HR.METRICS.SCHEDULED_ABSENT.HINT'),
  },
  {
    id: 'policy_alerts',
    label: t('WORKFORCE.ATTENDANCE.HR.METRICS.POLICY_ALERTS.LABEL'),
    value: String(policyAlertAgentsCount.value),
    hint: t('WORKFORCE.ATTENDANCE.HR.METRICS.POLICY_ALERTS.HINT', {
      total: policyAlertTotal.value,
    }),
  },
  {
    id: 'hours_logged',
    label: t('WORKFORCE.ATTENDANCE.HR.METRICS.HOURS_LOGGED.LABEL'),
    value: formatHours(totalWorkSeconds.value),
    suffix: 'h',
    hint: t('WORKFORCE.ATTENDANCE.HR.METRICS.HOURS_LOGGED.HINT'),
  },
]);

const openEditModal = session => {
  editModal.open = true;
  editModal.sessionId = session.id;
  editModal.form.startedAt = session.started_at
    ? dayjs(session.started_at).format('YYYY-MM-DDTHH:mm')
    : '';
  editModal.form.endedAt = session.ended_at
    ? dayjs(session.ended_at).format('YYYY-MM-DDTHH:mm')
    : '';
  editModal.form.breakHours = session.total_break_seconds
    ? (session.total_break_seconds / 3600).toFixed(2)
    : '';
  editModal.form.idleMinutes = session.total_idle_seconds
    ? (session.total_idle_seconds / 60).toFixed(1)
    : '';
  editModal.form.managerNotes = session.manager_notes || '';
};

const closeEditModal = () => {
  editModal.open = false;
  editModal.sessionId = null;
};

const submitEditModal = async () => {
  if (!editModal.sessionId) return;

  editModal.loading = true;
  try {
    const payload = {};
    if (editModal.form.startedAt) {
      payload.started_at = new Date(editModal.form.startedAt).toISOString();
    }
    if (editModal.form.endedAt || editModal.form.endedAt === '') {
      payload.ended_at = editModal.form.endedAt
        ? new Date(editModal.form.endedAt).toISOString()
        : null;
    }
    if (editModal.form.breakHours || editModal.form.breakHours === '') {
      const breakHours = numberOrNull(editModal.form.breakHours);
      payload.break_minutes =
        breakHours === null ? null : Math.round(breakHours * 60);
    }
    if (editModal.form.idleMinutes || editModal.form.idleMinutes === '') {
      const idleMinutes = numberOrNull(editModal.form.idleMinutes);
      payload.total_idle_seconds =
        idleMinutes === null ? null : Math.round(idleMinutes * 60);
    }
    if (editModal.form.managerNotes !== undefined) {
      payload.manager_notes = editModal.form.managerNotes;
    }

    await WorkforceAPI.updateAttendanceSession(editModal.sessionId, payload);
    await fetchAttendance();
    useAlert(t('WORKFORCE.ATTENDANCE.TOASTS.UPDATE_SUCCESS'));
    closeEditModal();
  } catch (error) {
    const message =
      resolveAttendanceActionError(error) ||
      t('WORKFORCE.ATTENDANCE.TOASTS.ACTION_ERROR');
    useAlert(message);
  } finally {
    editModal.loading = false;
  }
};
const selectedAgent = computed(() => {
  return (
    agents.value.find(agent => String(agent.id) === String(filters.userId)) ||
    null
  );
});

const dailySummaries = computed(() => {
  const bucket = new Map();
  sessions.value.forEach(session => {
    const key = session.started_at
      ? dayjs(session.started_at).format('YYYY-MM-DD')
      : 'untracked';
    if (!bucket.has(key)) {
      bucket.set(key, {
        work: 0,
        break: 0,
        idle: 0,
        count: 0,
      });
    }
    const entry = bucket.get(key);
    entry.work += session.total_work_seconds || 0;
    entry.break += session.total_break_seconds || 0;
    entry.idle += session.total_idle_seconds || 0;
    entry.count += 1;
  });
  return Array.from(bucket.entries())
    .map(([date, data]) => {
      const total = data.work + data.break + data.idle;
      return {
        date,
        label:
          date === 'untracked'
            ? t('WORKFORCE.ATTENDANCE.DAILY.UNTRACKED')
            : dayjs(date).format('MMM D, YYYY'),
        work: data.work,
        break: data.break,
        idle: data.idle,
        count: data.count,
        total,
        workPercent: total ? (data.work / total) * 100 : 0,
        breakPercent: total ? (data.break / total) * 100 : 0,
        idlePercent: total ? (data.idle / total) * 100 : 0,
      };
    })
    .sort((a, b) =>
      a.date === 'untracked'
        ? 1
        : dayjs(b.date).valueOf() - dayjs(a.date).valueOf()
    )
    .slice(0, 7);
});

const currentAgentSession = computed(() => {
  if (!selectedAgent.value) return null;
  return (
    sessions.value.find(
      session =>
        String(session.user_id) === String(selectedAgent.value.id) &&
        session.status !== 'closed'
    ) || null
  );
});

const currentSessionElapsed = computed(() => {
  const session = currentAgentSession.value;
  if (!session?.started_at) return t('WORKFORCE.STATUS.NO_ACTIVE_SHIFT');
  const started = dayjs(session.started_at);
  if (!started.isValid()) return '--';
  const seconds = Math.max(now.value.diff(started, 'second'), 0);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  const parts = [];
  if (hours) parts.push(`${hours}h`);
  parts.push(`${minutes.toString().padStart(2, '0')}m`);
  parts.push(`${remainingSeconds.toString().padStart(2, '0')}s`);
  return parts.join(' ');
});

const currentSessionStartLabel = computed(() => {
  const session = currentAgentSession.value;
  if (!session?.started_at) return '--';
  const started = dayjs(session.started_at);
  return started.isValid() ? started.format('MMM D, HH:mm') : '--';
});

const currentBreakElapsed = computed(() => {
  const session = currentAgentSession.value;
  const startedAt = session?.metadata?.active_break_started_at;
  if (!startedAt) return t('WORKFORCE.STATUS.NO_ACTIVE_BREAK');
  const started = dayjs(startedAt);
  if (!started.isValid()) return '--';
  const minutes = Math.max(now.value.diff(started, 'minute'), 0);
  return t('WORKFORCE.STATUS.BREAK_ELAPSED_MINUTES', { count: minutes });
});

const localClockLabel = computed(() => now.value.format('HH:mm:ss'));

const currentSessionTimezone = computed(() => {
  const session = currentAgentSession.value;
  return session?.timezone || session?.metadata?.timezone || 'UTC';
});

const canRunManagerAction = action => {
  if (!selectedAgent.value) return false;
  const openSession = currentAgentSession.value;
  switch (action) {
    case 'check_in':
      return !openSession;
    case 'start_break':
      return Boolean(
        openSession && !openSession.metadata?.active_break_started_at
      );
    case 'end_break':
      return Boolean(
        openSession && openSession.metadata?.active_break_started_at
      );
    case 'check_out':
    case 'force_close':
      return Boolean(openSession);
    default:
      return false;
  }
};

const resolveAttendanceActionError = error => {
  const code = error?.response?.data?.error;
  if (!code) return null;
  const dictionary = {
    session_already_open: t('WORKFORCE.TOASTS.SESSION_ALREADY_OPEN'),
    no_open_session: t('WORKFORCE.TOASTS.NO_OPEN_SESSION'),
    break_already_active: t('WORKFORCE.TOASTS.BREAK_ALREADY_ACTIVE'),
    break_not_active: t('WORKFORCE.TOASTS.BREAK_NOT_ACTIVE'),
    forbidden: t('WORKFORCE.ATTENDANCE.TOASTS.ACTION_ERROR'),
    invalid_started_at: t('WORKFORCE.ATTENDANCE.TOASTS.INVALID_STARTED_AT'),
    invalid_ended_at: t('WORKFORCE.ATTENDANCE.TOASTS.INVALID_ENDED_AT'),
    invalid_time_range: t('WORKFORCE.ATTENDANCE.TOASTS.INVALID_TIME_RANGE'),
  };
  return dictionary[code] || null;
};

const runManagerAction = async action => {
  if (!selectedAgent.value) {
    useAlert(t('WORKFORCE.ATTENDANCE.TOASTS.SELECT_AGENT'));
    return;
  }

  const payload = {
    user_id: selectedAgent.value.id,
    timestamp: new Date().toISOString(),
    source: 'manual',
  };

  try {
    switch (action) {
      case 'check_in': {
        await WorkforceAPI.checkIn(payload);
        useAlert(t('WORKFORCE.ATTENDANCE.TOASTS.CHECK_IN'));
        break;
      }
      case 'start_break': {
        await WorkforceAPI.startBreak(payload);
        useAlert(t('WORKFORCE.ATTENDANCE.TOASTS.START_BREAK'));
        break;
      }
      case 'end_break': {
        await WorkforceAPI.endBreak(payload);
        useAlert(t('WORKFORCE.ATTENDANCE.TOASTS.END_BREAK'));
        break;
      }
      case 'check_out': {
        await WorkforceAPI.checkOut({
          ...payload,
          notes: t('WORKFORCE.ATTENDANCE.MANAGER_NOTE'),
        });
        useAlert(t('WORKFORCE.ATTENDANCE.TOASTS.CHECK_OUT'));
        break;
      }
      case 'force_close': {
        await WorkforceAPI.forceClose({
          ...payload,
          reason: 'manager_override',
        });
        useAlert(t('WORKFORCE.ATTENDANCE.TOASTS.FORCE_CLOSE'));
        break;
      }
      default:
        break;
    }
    await fetchAttendance();
    emit('refresh-self-status');
  } catch (error) {
    const message =
      resolveAttendanceActionError(error) ||
      t('WORKFORCE.ATTENDANCE.TOASTS.ACTION_ERROR');
    useAlert(message);
  }
};

const eventLabel = eventType => {
  const key = `WORKFORCE.ATTENDANCE.EVENTS.${eventType.toUpperCase()}`;
  return t(key, eventType);
};

watch(
  () => [filters.from, filters.to],
  ([from, to]) => {
    if (!from || !to) {
      selectedDateRange.value = 'custom';
      return;
    }

    const match = dateRangePresets.value.find(preset => {
      const bounds = preset.getBounds();
      return bounds.from === from && bounds.to === to;
    });

    selectedDateRange.value = match ? match.key : 'custom';
    if (to) {
      selectedAnomalyDate.value = to;
    }
  }
);

watch(
  () => policyModal.open,
  open => {
    if (open) {
      resetSettingsForm();
    }
  }
);

watch(
  () => ({ ...filters }),
  () => {
    fetchAttendance();
  },
  { deep: true }
);

onMounted(async () => {
  ticker = setInterval(() => {
    now.value = dayjs();
  }, 1000);
  if (!agents.value.length) {
    await store.dispatch('agents/get');
  }
  await Promise.all([
    fetchAttendanceSettings(),
    fetchAttendance(),
    fetchLiveStatus(),
  ]);
});

onBeforeUnmount(() => {
  if (ticker) {
    clearInterval(ticker);
    ticker = null;
  }
});

const agentName = userId => {
  const agent = agents.value.find(item => String(item.id) === String(userId));
  return agent ? agent.name : t('WORKFORCE.LABELS.UNKNOWN_AGENT');
};
</script>

<template>
  <section class="flex flex-col gap-5 px-8 py-5 lg:px-10">
    <!-- HR Metric Cards -->
    <div class="space-y-3">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <span
            class="text-xs font-semibold uppercase tracking-wide text-n-slate-9"
          >
            {{ t('WORKFORCE.ATTENDANCE.HR.OVERVIEW_TITLE') }}
          </span>
          <span
            v-if="liveLoading"
            class="i-lucide-loader-2 animate-spin text-n-slate-9"
            aria-hidden="true"
          />
        </div>
        <span class="text-xs text-n-slate-9">
          {{ t('WORKFORCE.ATTENDANCE.HR.OVERVIEW_HINT') }}
        </span>
      </div>
      <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        <div
          v-for="metric in hrMetricCards"
          :key="metric.id"
          class="relative overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 p-4 shadow-sm"
        >
          <div
            class="absolute left-0 top-0 h-full w-1 rounded-l-xl"
            :class="{
              'bg-indigo-9': metric.id === 'team_check_ins',
              'bg-teal-9': metric.id === 'active_now',
              'bg-amber-9': metric.id === 'active_breaks',
              'bg-rose-9': metric.id === 'scheduled_absent',
              'bg-ruby-9': metric.id === 'policy_alerts',
              'bg-slate-7': metric.id === 'hours_logged',
            }"
          />
          <p
            class="text-[11px] font-medium uppercase tracking-wide text-n-slate-9"
          >
            {{ metric.label }}
          </p>
          <div
            class="mt-2 flex items-baseline gap-1.5 text-2xl font-bold text-n-slate-12"
          >
            <span>{{ metric.value }}</span>
            <span
              v-if="metric.suffix"
              class="text-sm font-medium text-n-slate-10"
            >
              {{ metric.suffix }}
            </span>
          </div>
          <p v-if="metric.hint" class="mt-1 text-[11px] text-n-slate-9">
            {{ metric.hint }}
          </p>
        </div>
      </div>
    </div>

    <div class="grid gap-5 lg:grid-cols-[2fr_1fr]">
      <div class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm">
        <div class="space-y-4 border-b border-n-weak/60 px-5 py-4">
          <div class="grid gap-4 lg:grid-cols-5">
            <label
              class="grid gap-1 text-xs uppercase tracking-wide text-n-slate-10"
            >
              {{ t('WORKFORCE.FILTERS.AGENT') }}
              <select
                v-model="filters.userId"
                class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
              >
                <option value="all">
                  {{ t('WORKFORCE.FILTERS.ALL_AGENTS') }}
                </option>
                <option
                  v-for="agent in agents"
                  :key="agent.id"
                  :value="agent.id"
                >
                  {{ agent.name }}
                </option>
              </select>
            </label>
            <label
              class="grid gap-1 text-xs uppercase tracking-wide text-n-slate-10"
            >
              {{ t('WORKFORCE.FILTERS.FROM') }}
              <input
                v-model="filters.from"
                type="date"
                class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
              />
            </label>
            <label
              class="grid gap-1 text-xs uppercase tracking-wide text-n-slate-10"
            >
              {{ t('WORKFORCE.FILTERS.TO') }}
              <input
                v-model="filters.to"
                type="date"
                class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
              />
            </label>
            <div class="col-span-2 flex items-end justify-end gap-2">
              <WorkforceButton
                variant="ghost"
                size="sm"
                icon="i-lucide-eraser"
                @click="clearDateFilters"
              >
                {{ t('WORKFORCE.ATTENDANCE.ACTIONS.CLEAR_DATES') }}
              </WorkforceButton>
              <WorkforceButton
                variant="ghost"
                size="sm"
                icon="i-lucide-refresh-cw"
                :is-loading="loading"
                @click="handleRefresh"
              >
                {{ t('WORKFORCE.ACTIONS.REFRESH') }}
              </WorkforceButton>
            </div>
          </div>
          <div
            class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <div class="flex flex-wrap items-center gap-2">
              <WorkforceButton
                v-for="preset in dateRangePresets"
                :key="preset.key"
                variant="ghost"
                size="xs"
                class="transition"
                :class="[
                  isRangeActive(preset.key)
                    ? 'bg-black text-white shadow-sm dark:bg-white dark:text-black'
                    : 'text-n-slate-11 hover:bg-black/5 hover:text-black dark:text-n-slate-9 dark:hover:bg-white/10 dark:hover:text-white',
                ]"
                @click="setDateRange(preset.key)"
              >
                {{ preset.label }}
              </WorkforceButton>
            </div>
            <div
              class="flex flex-col items-start gap-2 text-xs text-n-slate-9 sm:items-end sm:text-right"
            >
              <span>{{ policySummaryText }}</span>
              <WorkforceButton
                size="xs"
                variant="solid"
                color="emerald"
                :is-loading="settingsLoading"
                icon="i-lucide-sliders-horizontal"
                @click="openPolicyModal"
              >
                {{ t('WORKFORCE.ATTENDANCE.SETTINGS.CTA') }}
              </WorkforceButton>
            </div>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-n-weak text-sm">
            <thead
              class="bg-n-solid-2 text-left text-xs uppercase tracking-wide text-n-slate-10"
            >
              <tr>
                <th class="w-12 px-2 py-3" />
                <th class="px-5 py-3">{{ t('WORKFORCE.TABLE.AGENT') }}</th>
                <th class="px-5 py-3">
                  {{ t('WORKFORCE.ATTENDANCE.TABLE.STARTED_AT') }}
                </th>
                <th class="px-5 py-3">
                  {{ t('WORKFORCE.ATTENDANCE.TABLE.ENDED_AT') }}
                </th>
                <th class="px-5 py-3">
                  {{ t('WORKFORCE.ATTENDANCE.TABLE.DURATION') }}
                </th>
                <th class="px-5 py-3">
                  {{ t('WORKFORCE.ATTENDANCE.TABLE.BREAK') }}
                </th>
                <th class="px-5 py-3">
                  {{ t('WORKFORCE.ATTENDANCE.TABLE.IDLE') }}
                </th>
                <th class="px-5 py-3">
                  {{ t('WORKFORCE.ATTENDANCE.TABLE.ANOMALIES') }}
                </th>
                <th class="px-5 py-3">{{ t('WORKFORCE.TABLE.STATUS') }}</th>
                <th class="px-5 py-3 text-right">
                  {{ t('WORKFORCE.TABLE.ACTIONS') }}
                </th>
              </tr>
            </thead>
            <tbody
              class="divide-y divide-n-weak/70 bg-n-solid-3 text-n-slate-12"
            >
              <tr v-if="loading">
                <td
                  colspan="10"
                  class="px-5 py-6 text-center text-sm text-n-slate-10"
                >
                  {{ t('WORKFORCE.ATTENDANCE.TABLE.LOADING') }}
                </td>
              </tr>
              <tr v-else-if="!sessions.length">
                <td
                  colspan="10"
                  class="px-5 py-6 text-center text-sm text-n-slate-10"
                >
                  {{ t('WORKFORCE.ATTENDANCE.TABLE.EMPTY') }}
                </td>
              </tr>
              <template v-for="session in sessions" :key="session.id">
                <tr :class="sessionRowClass(session)">
                  <td class="px-2 py-3 align-top">
                    <WorkforceButton
                      size="xs"
                      variant="ghost"
                      icon="i-lucide-chevron-down"
                      :class="{ 'rotate-180': isSessionExpanded(session.id) }"
                      @click="toggleSessionExpansion(session.id)"
                    />
                  </td>
                  <td class="px-5 py-3 font-medium">
                    {{ agentName(session.user_id) }}
                  </td>
                  <td class="px-5 py-3">
                    {{ formatDateTime(session.started_at) }}
                  </td>
                  <td class="px-5 py-3">
                    {{ formatDateTime(session.ended_at) }}
                  </td>
                  <td class="px-5 py-3">
                    {{ formatDuration(session.total_work_seconds) }}
                  </td>
                  <td class="px-5 py-3">
                    {{ formatDuration(session.total_break_seconds) }}
                  </td>
                  <td class="px-5 py-3">
                    {{ formatDuration(session.total_idle_seconds) }}
                  </td>
                  <td class="px-5 py-3">
                    <div
                      v-if="session.anomalies?.length"
                      class="flex flex-col gap-1"
                    >
                      <span
                        v-for="flag in session.anomalies"
                        :key="`${session.id}-${flag.code}`"
                        class="inline-flex items-center gap-2 rounded-lg border border-red-8/40 bg-red-3/30 px-2 py-1 text-[11px] font-medium text-red-11 dark:border-red-5/50 dark:bg-red-7/15 dark:text-red-3"
                      >
                        <span class="i-lucide-alert-triangle text-xs" />
                        {{ anomalyLabel(flag) }}
                      </span>
                    </div>
                    <span v-else class="text-xs text-n-slate-9">
                      {{ t('WORKFORCE.ATTENDANCE.TABLE.ANOMALIES_NONE') }}
                    </span>
                  </td>
                  <td class="px-5 py-3">
                    <span
                      class="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs"
                      :class="[
                        session.status === 'open'
                          ? 'bg-amber-3/40 text-amber-11'
                          : session.status === 'closed'
                            ? 'bg-black/80 text-white'
                            : 'bg-red-3/30 text-red-11',
                      ]"
                    >
                      <span
                        class="size-2 rounded-full"
                        :class="[
                          session.status === 'open'
                            ? 'bg-amber-10'
                            : session.status === 'closed'
                              ? 'bg-white'
                              : 'bg-red-9',
                        ]"
                      />
                      {{ session.status }}
                    </span>
                  </td>
                  <td class="px-5 py-3 text-right">
                    <div class="flex justify-end gap-2">
                      <WorkforceButton
                        size="xs"
                        variant="ghost"
                        icon="i-lucide-pencil"
                        @click="openEditModal(session)"
                      >
                        {{ t('WORKFORCE.ATTENDANCE.ACTIONS.EDIT') }}
                      </WorkforceButton>
                      <WorkforceButton
                        v-if="
                          session.status !== 'closed' &&
                          filters.userId !== 'all' &&
                          String(filters.userId) === String(session.user_id)
                        "
                        size="xs"
                        variant="ghost"
                        icon="i-lucide-shield-alert"
                        @click="runManagerAction('force_close')"
                      >
                        {{ t('WORKFORCE.ATTENDANCE.ACTIONS.FORCE_CLOSE') }}
                      </WorkforceButton>
                    </div>
                  </td>
                </tr>
                <tr
                  v-if="isSessionExpanded(session.id)"
                  class="bg-n-solid-2/60"
                >
                  <td />
                  <td colspan="9" class="px-5 pb-4">
                    <div
                      v-if="session.events && session.events.length"
                      class="grid gap-2"
                    >
                      <div
                        v-for="event in session.events"
                        :key="event.id"
                        class="flex flex-wrap items-center justify-between rounded-lg border border-n-weak/50 bg-n-solid-1 px-3 py-2 text-xs"
                      >
                        <div class="flex items-center gap-2">
                          <span
                            class="inline-flex size-6 items-center justify-center rounded-full bg-black/80 text-white dark:bg-white dark:text-black"
                          >
                            <span class="i-lucide-clock size-4" />
                          </span>
                          <div>
                            <p class="font-medium text-n-slate-12">
                              {{ eventLabel(event.event_type) }}
                            </p>
                            <p class="text-n-slate-10">
                              {{ formatDateTime(event.event_time) }}
                            </p>
                          </div>
                        </div>
                        <div class="text-right text-n-slate-10">
                          <p v-if="event.metadata?.break_duration_seconds">
                            {{
                              t('WORKFORCE.ATTENDANCE.EVENTS.BREAK_DURATION', {
                                minutes: Math.round(
                                  event.metadata.break_duration_seconds / 60
                                ),
                              })
                            }}
                          </p>
                          <p v-if="event.notes">
                            {{ event.notes }}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-xs text-n-slate-10">
                      {{ t('WORKFORCE.ATTENDANCE.EVENTS.EMPTY') }}
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <div class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm">
          <div class="border-b border-n-weak/60 px-5 py-3">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('WORKFORCE.ATTENDANCE.MANAGER_ACTIONS') }}
            </h3>
          </div>
          <div class="p-5">
            <p v-if="!selectedAgent" class="text-sm text-n-slate-10">
              {{ t('WORKFORCE.ATTENDANCE.ACTIONS.SELECT_HINT') }}
            </p>
            <div v-else class="space-y-4">
              <div
                class="rounded-2xl border border-n-weak/60 bg-n-solid-1 px-5 py-4 shadow-sm dark:border-white/20 dark:bg-white/10"
              >
                <div
                  class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
                >
                  <div class="space-y-2">
                    <p class="text-base font-semibold text-n-slate-12">
                      {{ selectedAgent.name }}
                    </p>
                    <span
                      class="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide transition"
                      :class="
                        currentAgentSession
                          ? 'bg-emerald-3/40 text-emerald-11'
                          : 'bg-n-solid-2 text-n-slate-10'
                      "
                    >
                      <span
                        :class="
                          currentAgentSession
                            ? 'i-lucide-activity'
                            : 'i-lucide-moon'
                        "
                      />
                      {{
                        currentAgentSession
                          ? t('WORKFORCE.ATTENDANCE.STATUS.ON_SHIFT')
                          : t('WORKFORCE.ATTENDANCE.STATUS.OFF_SHIFT')
                      }}
                    </span>
                    <p class="text-xs text-n-slate-9">
                      {{
                        currentAgentSession
                          ? t('WORKFORCE.ATTENDANCE.STATUS.SHIFT_STARTED_AT', {
                              label: currentSessionStartLabel,
                            })
                          : t('WORKFORCE.STATUS.NO_ACTIVE_SHIFT')
                      }}
                    </p>
                  </div>
                  <div
                    class="rounded-xl border border-n-weak/60 bg-n-solid-2 px-3 py-2 text-right text-[11px] text-n-slate-10 dark:border-white/20 dark:bg-white/10"
                  >
                    <p class="text-sm font-semibold text-n-slate-12">
                      {{ localClockLabel }}
                    </p>
                    <p>
                      {{
                        t('WORKFORCE.ATTENDANCE.STATUS.TIMEZONE', {
                          tz: currentSessionTimezone,
                        })
                      }}
                    </p>
                  </div>
                </div>
                <div class="mt-4 grid gap-3 sm:grid-cols-2">
                  <div
                    class="rounded-xl border border-n-weak/50 bg-white/80 px-4 py-3 shadow-sm dark:border-white/20 dark:bg-white/10"
                  >
                    <p
                      class="text-[11px] uppercase tracking-wide text-n-slate-9"
                    >
                      {{ t('WORKFORCE.ATTENDANCE.STATUS.SHIFT_ELAPSED') }}
                    </p>
                    <p class="mt-2 text-lg font-semibold text-n-slate-12">
                      {{ currentSessionElapsed }}
                    </p>
                  </div>
                  <div
                    class="rounded-xl border border-n-weak/50 bg-white/80 px-4 py-3 shadow-sm dark:border-white/20 dark:bg-white/10"
                  >
                    <p
                      class="text-[11px] uppercase tracking-wide text-n-slate-9"
                    >
                      {{ t('WORKFORCE.ATTENDANCE.STATUS.BREAK_TIMER') }}
                    </p>
                    <p class="mt-2 text-lg font-semibold text-n-slate-12">
                      {{ currentBreakElapsed }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="flex flex-wrap gap-2">
                <WorkforceButton
                  size="sm"
                  variant="solid"
                  icon="i-lucide-play"
                  :disabled="!canRunManagerAction('check_in')"
                  @click="runManagerAction('check_in')"
                >
                  {{ t('WORKFORCE.ACTIONS.START_SHIFT') }}
                </WorkforceButton>
                <WorkforceButton
                  size="sm"
                  variant="solid"
                  icon="i-lucide-mug-hot"
                  :disabled="!canRunManagerAction('start_break')"
                  @click="runManagerAction('start_break')"
                >
                  {{ t('WORKFORCE.ACTIONS.START_BREAK') }}
                </WorkforceButton>
                <WorkforceButton
                  size="sm"
                  variant="solid"
                  icon="i-lucide-coffee"
                  :disabled="!canRunManagerAction('end_break')"
                  @click="runManagerAction('end_break')"
                >
                  {{ t('WORKFORCE.ACTIONS.END_BREAK') }}
                </WorkforceButton>
                <WorkforceButton
                  size="sm"
                  variant="solid"
                  icon="i-lucide-stop-circle"
                  :disabled="!canRunManagerAction('check_out')"
                  @click="runManagerAction('check_out')"
                >
                  {{ t('WORKFORCE.ACTIONS.END_SHIFT') }}
                </WorkforceButton>
                <WorkforceButton
                  size="sm"
                  variant="solid"
                  icon="i-lucide-shield-alert"
                  :disabled="!canRunManagerAction('force_close')"
                  @click="runManagerAction('force_close')"
                >
                  {{ t('WORKFORCE.ATTENDANCE.ACTIONS.FORCE_CLOSE') }}
                </WorkforceButton>
              </div>
            </div>
          </div>
        </div>

        <div class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm">
          <div
            class="flex items-center justify-between border-b border-n-weak/60 px-5 py-3"
          >
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('WORKFORCE.ATTENDANCE.DAILY.TITLE') }}
            </h3>
            <span class="text-xs text-n-slate-10">
              {{ t('WORKFORCE.ATTENDANCE.DAILY.SUBTITLE') }}
            </span>
          </div>
          <div class="grid gap-2 px-5 py-4">
            <div
              v-for="day in dailySummaries"
              :key="day.date"
              role="button"
              class="cursor-pointer rounded-lg border border-n-weak/60 bg-n-solid-3 px-4 py-3 transition hover:border-n-strong hover:bg-n-solid-1"
              :class="[
                day.date === selectedAnomalyDate
                  ? 'border-emerald-500 ring-1 ring-emerald-500/40 bg-emerald-3/20 dark:border-emerald-5 dark:bg-emerald-5/20'
                  : '',
                day.date === 'untracked'
                  ? 'cursor-default opacity-60 hover:bg-n-solid-2 hover:border-n-weak/60'
                  : '',
              ]"
              :aria-pressed="day.date === selectedAnomalyDate"
              @click="selectSummaryDay(day)"
            >
              <div
                class="flex items-center justify-between text-sm text-n-slate-12"
              >
                <span class="font-medium">{{ day.label }}</span>
                <span>{{ formatDuration(day.total) }}</span>
              </div>
              <div
                class="mt-2 h-2 overflow-hidden rounded-full bg-n-weak/40 flex"
              >
                <span
                  class="h-full bg-black"
                  :style="{ width: `${Math.min(day.workPercent, 100)}%` }"
                />
                <span
                  class="h-full bg-amber-500/70"
                  :style="{ width: `${Math.min(day.breakPercent, 100)}%` }"
                />
                <span
                  class="h-full bg-red-500/50"
                  :style="{ width: `${Math.min(day.idlePercent, 100)}%` }"
                />
              </div>
              <div class="mt-2 flex justify-between text-[11px] text-n-slate-9">
                <span>{{
                  t('WORKFORCE.ATTENDANCE.DAILY.WORK', {
                    value: formatDuration(day.work),
                  })
                }}</span>
                <span>{{
                  t('WORKFORCE.ATTENDANCE.DAILY.BREAK', {
                    value: formatDuration(day.break),
                  })
                }}</span>
                <span>{{
                  t('WORKFORCE.ATTENDANCE.DAILY.IDLE', {
                    value: formatDuration(day.idle),
                  })
                }}</span>
              </div>
            </div>
            <p v-if="!dailySummaries.length" class="text-sm text-n-slate-10">
              {{ t('WORKFORCE.ATTENDANCE.DAILY.EMPTY') }}
            </p>
          </div>
          <!-- Anomaly panel -->
          <div class="border-t border-n-weak/60 px-5 py-4">
            <div class="flex items-center justify-between">
              <h4 class="text-sm font-semibold text-n-slate-12">
                {{
                  t('WORKFORCE.ATTENDANCE.ANOMALY_PANEL.TITLE', {
                    date: selectedAnomalyDateLabel,
                  })
                }}
              </h4>
              <span class="text-xs text-n-slate-10">
                {{ t('WORKFORCE.ATTENDANCE.ANOMALY_PANEL.SUBTITLE') }}
              </span>
            </div>
            <div v-if="selectedAnomalies.length" class="mt-3 space-y-3">
              <div
                v-for="item in selectedAnomalies"
                :key="item.id"
                class="rounded-xl border border-red-8/40 bg-red-3/20 px-4 py-3 text-sm text-red-11 shadow-sm dark:border-red-5/50 dark:bg-red-7/20 dark:text-red-2"
              >
                <div
                  class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between"
                >
                  <div class="flex items-center gap-2">
                    <span
                      class="inline-flex size-6 items-center justify-center rounded-full bg-red-10/90 text-white dark:bg-red-4 dark:text-black"
                    >
                      <span class="i-lucide-alert-triangle text-xs" />
                    </span>
                    <div>
                      <p class="font-semibold">
                        {{ item.agentName }}
                      </p>
                      <p class="text-xs text-red-11/80 dark:text-red-2/80">
                        {{ item.anomalyLabel }}
                      </p>
                    </div>
                  </div>
                  <span
                    class="text-[11px] uppercase tracking-wide text-red-11/70 dark:text-red-2/70"
                  >
                    {{ item.statusLabel || '' }}
                  </span>
                </div>
                <div
                  class="mt-3 grid gap-2 text-xs text-red-11/90 dark:text-red-2/80 sm:grid-cols-2"
                >
                  <span>{{
                    t('WORKFORCE.ATTENDANCE.ANOMALY_PANEL.STARTED_AT', {
                      time: item.startedAt,
                    })
                  }}</span>
                  <span>{{
                    t('WORKFORCE.ATTENDANCE.ANOMALY_PANEL.ENDED_AT', {
                      time: item.endedAt,
                    })
                  }}</span>
                  <span v-if="item.workSummary">
                    {{
                      t('WORKFORCE.ATTENDANCE.ANOMALY_PANEL.WORK_RECORDED', {
                        actual: item.workSummary.actual,
                        required: item.workSummary.required,
                      })
                    }}
                  </span>
                  <span v-else>
                    {{
                      t('WORKFORCE.ATTENDANCE.ANOMALY_PANEL.BREAK_RECORDED', {
                        actual: item.breakDuration,
                      })
                    }}
                  </span>
                  <span v-if="item.breakSummary">
                    {{
                      t('WORKFORCE.ATTENDANCE.ANOMALY_PANEL.BREAK_REQUIRED', {
                        actual: item.breakSummary.actual,
                        required: item.breakSummary.required,
                      })
                    }}
                  </span>
                </div>
              </div>
            </div>
            <div
              v-else
              class="mt-3 rounded-lg border border-dashed border-n-weak/50 bg-n-solid-3 px-4 py-3 text-xs text-n-slate-10"
            >
              {{ t('WORKFORCE.ATTENDANCE.ANOMALY_PANEL.EMPTY') }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <WorkforceModal
      v-model="policyModal.open"
      :title="t('WORKFORCE.ATTENDANCE.SETTINGS.MODAL_TITLE')"
      :description="t('WORKFORCE.ATTENDANCE.SETTINGS.MODAL_DESCRIPTION')"
      :confirm-label="t('WORKFORCE.ATTENDANCE.SETTINGS.ACTIONS.SAVE')"
      :cancel-label="t('WORKFORCE.ATTENDANCE.SETTINGS.ACTIONS.CANCEL')"
      :loading="settingsSaving"
      width="lg"
      @confirm="saveAttendanceSettings"
      @cancel="closePolicyModal"
    >
      <div class="grid gap-4 sm:grid-cols-2">
        <label
          class="grid gap-1 text-xs uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.ATTENDANCE.SETTINGS.MIN_SHIFT_HOURS') }}
          <input
            v-model="settingsForm.minShiftHours"
            type="number"
            step="0.25"
            min="0"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 disabled:cursor-not-allowed disabled:bg-n-solid-2 dark:focus:border-white dark:focus:ring-white/30"
            placeholder="6"
          />
          <span class="text-[11px] text-n-slate-9">
            {{ t('WORKFORCE.ATTENDANCE.SETTINGS.MIN_SHIFT_HINT') }}
          </span>
        </label>
        <label
          class="grid gap-1 text-xs uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.ATTENDANCE.SETTINGS.MIN_BREAK_HOURS') }}
          <input
            v-model="settingsForm.minBreakHours"
            type="number"
            step="0.25"
            min="0"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 disabled:cursor-not-allowed disabled:bg-n-solid-2 dark:focus:border-white dark:focus:ring-white/30"
            placeholder="1"
          />
          <span class="text-[11px] text-n-slate-9">
            {{ t('WORKFORCE.ATTENDANCE.SETTINGS.MIN_BREAK_HINT') }}
          </span>
        </label>
      </div>
      <div class="mt-3 flex justify-end">
        <WorkforceButton
          variant="ghost"
          size="xs"
          :disabled="settingsLoading || settingsSaving || !settingsDirty"
          @click="resetSettingsForm"
        >
          {{ t('WORKFORCE.ATTENDANCE.SETTINGS.ACTIONS.RESET') }}
        </WorkforceButton>
      </div>
      <p class="mt-3 text-xs text-n-slate-9">
        {{ t('WORKFORCE.ATTENDANCE.SETTINGS.MODAL_HELP') }}
      </p>
    </WorkforceModal>
    <WorkforceModal
      v-model="editModal.open"
      :title="t('WORKFORCE.ATTENDANCE.EDIT_MODAL.TITLE')"
      :description="t('WORKFORCE.ATTENDANCE.EDIT_MODAL.DESCRIPTION')"
      :confirm-label="t('WORKFORCE.ATTENDANCE.EDIT_MODAL.SAVE')"
      :cancel-label="t('WORKFORCE.ATTENDANCE.EDIT_MODAL.CANCEL')"
      :loading="editModal.loading"
      width="lg"
      @confirm="submitEditModal"
      @cancel="closeEditModal"
    >
      <div class="grid gap-4 sm:grid-cols-2">
        <label
          class="grid gap-1 text-xs uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.ATTENDANCE.EDIT_MODAL.START') }}
          <input
            v-model="editModal.form.startedAt"
            type="datetime-local"
            step="60"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
          />
        </label>
        <label
          class="grid gap-1 text-xs uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.ATTENDANCE.EDIT_MODAL.END') }}
          <input
            v-model="editModal.form.endedAt"
            type="datetime-local"
            step="60"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
          />
        </label>
        <label
          class="grid gap-1 text-xs uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.ATTENDANCE.EDIT_MODAL.BREAK_HOURS') }}
          <input
            v-model="editModal.form.breakHours"
            type="number"
            step="0.25"
            min="0"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
          />
        </label>
        <label
          class="grid gap-1 text-xs uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.ATTENDANCE.EDIT_MODAL.IDLE_MINUTES') }}
          <input
            v-model="editModal.form.idleMinutes"
            type="number"
            step="1"
            min="0"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
          />
        </label>
        <label
          class="grid gap-1 text-xs uppercase tracking-wide text-n-slate-10 sm:col-span-2"
        >
          {{ t('WORKFORCE.ATTENDANCE.EDIT_MODAL.MANAGER_NOTES') }}
          <textarea
            v-model="editModal.form.managerNotes"
            rows="3"
            class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
          />
        </label>
      </div>
    </WorkforceModal>
  </section>
</template>
